import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import { getUploadUrl } from '../../utils/config';
import { FaPen } from 'react-icons/fa';
import MemberEditModal from '../../components/MemberEditModal';

export default function UserProfile() {
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user?.memberId) {
      API.get(`/members/${user.memberId}`)
        .then(res => setMember(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const Section = ({ title, icon, fields }) => (
    <div style={{ marginBottom: 24 }}>
      <div className="form-section-title">{icon} {title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {fields.map((f, i) => (
          <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{f.label}</div>
            <div style={{ fontSize: 14, color: f.value ? '#111827' : '#d1d5db', fontWeight: f.value ? 500 : 400 }}>{f.value || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>My Profile</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>Your complete membership information</p>
        </div>
        {member && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setIsEditing(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FaPen size={12} /> Edit Profile
          </button>
        )}
      </div>
      <div className="page-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner"></div></div>
        ) : !member ? (
          <div className="empty-state">
            <div className="empty-state-icon">🤔</div>
            <h3>Profile not found</h3>
            <p>Contact admin for assistance</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>
            {/* Left Card - Photo */}
            <div className="card" style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ marginBottom: 16 }}>
                {member.photo_path ? (
                  <img
                    src={getUploadUrl(member.photo_path)}
                    alt="Member Photo"
                    style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '3px solid #bbf7d0', boxShadow: '0 4px 12px rgba(22,163,74,0.2)' }}
                  />
                ) : (
                  <div style={{
                    width: 120, height: 120, borderRadius: '50%', margin: '0 auto',
                    background: 'linear-gradient(135deg, #15803d, #22c55e)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 48, color: 'white', fontWeight: 700
                  }}>
                    {member.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <h2 style={{ fontWeight: 700, fontSize: 18, color: '#111827', marginBottom: 4 }}>{member.name}</h2>
              <div style={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                {member.member_id}
              </div>
              <span className={`badge ${member.status === 'approved' ? 'badge-success' : member.status === 'pending' ? 'badge-pending' : 'badge-danger'}`}>
                {member.status}
              </span>
              {member.category && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                  <strong>Category:</strong> {member.category}
                </div>
              )}
              {member.signature_path && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>Signature</div>
                  <img
                    src={getUploadUrl(member.signature_path)}
                    alt="Signature"
                    style={{ maxWidth: '100%', height: 50, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: 4 }}
                  />
                </div>
              )}
              <div style={{ marginTop: 16, padding: '12px', background: '#f0fdf4', borderRadius: 8, fontSize: 12, color: '#6b7280', textAlign: 'left' }}>
                <div><strong>Registered:</strong> {member.created_at?.split('T')[0] || member.created_at?.split(' ')[0]}</div>
              </div>
            </div>

            {/* Right - Details */}
            <div className="card" style={{ padding: 24 }}>
              <Section title="Personal Information" icon="👤" fields={[
                { label: 'Name', value: member.name },
                { label: 'Father Name', value: member.father_name },
                { label: 'Mother Name', value: member.mother_name },
                { label: 'Spouse Name', value: member.spouse_name },
              ]} />

              <Section title="Address Details" icon="📍" fields={[
                { label: 'Full Address', value: member.full_address },
                { label: 'Village', value: member.village },
                { label: 'Post', value: member.post },
                { label: 'Hobli', value: member.hobli },
                { label: 'Taluk', value: member.taluk },
                { label: 'District', value: member.district },
                { label: 'PIN Code', value: member.pin },
              ]} />

              <Section title="Contact Information" icon="📞" fields={[
                { label: 'Mobile Phone', value: member.mobile_phone },
                { label: 'Email', value: member.email },
              ]} />

              <Section title="Identity Documents" icon="🪪" fields={[
                { label: 'Aadhaar No', value: member.aadhaar_no ? '••••' + member.aadhaar_no.slice(-4) : null },
                { label: 'PAN', value: member.pan },
                { label: 'Voter ID', value: member.voter_id },
                { label: 'Ration Card No', value: member.ration_card_no },
                { label: 'Driving License', value: member.driving_license_no },
                { label: 'Passport No', value: member.passport_no },
              ]} />
            </div>
          </div>
        )}
      </div>

      {/* Member Edit Modal (Aadhaar locked for regular users) */}
      {isEditing && member && (
        <MemberEditModal
          member={member}
          isAdmin={false}
          onClose={() => setIsEditing(false)}
          onSuccess={(updated) => setMember(updated)}
        />
      )}
    </div>
  );
}
